import React, { useState, useEffect } from 'react';
import { Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import {
    FiPlus,
    FiSearch,
    FiUser,
    FiMail,
    FiShield,
    FiKey,
    FiCalendar,
    FiUserX,
    FiAlertTriangle,
    FiEdit2
} from 'react-icons/fi';
import api from '../../services/api';
import SectionHelpModal from '../../components/common/SectionHelpModal';
import { useLocation } from 'react-router-dom';
import AppTour from '../../components/common/AppTour';
import Pagination from '../../components/common/Pagination';

const Employees = () => {
    const location = useLocation();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: ''
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [submissionLoading, setSubmissionLoading] = useState(false);
    const [runTour, setRunTour] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'empleados') {
            setRunTour(true);
        }
    }, [location.search]);

    // Efecto para buscar y paginar
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchEmployees(currentPage, searchTerm);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [currentPage, searchTerm]);

    const fetchEmployees = async (page = 1, search = '') => {
        try {
            const response = await api.get('/usuarios/');
            // Robust extraction: Check if array, check for results, or default to empty array
            const data = response.data;
            const list = Array.isArray(data) ? data : (data?.results || []);
            setEmployees(list);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching employees:", err);
            setError("No se pudo cargar la lista de empleados.");
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingEmployee(null);
        setFormData({ first_name: '', last_name: '', email: '' });
        setError(null);
        setSuccess(null);
    };

    const handleEdit = (emp) => {
        setFormData({
            first_name: emp.first_name,
            last_name: emp.last_name,
            email: emp.email
        });
        setEditingEmployee(emp.id);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmissionLoading(true);
        setError(null);
        setSuccess(null);

        try {
            if (editingEmployee) {
                await api.put(`/usuarios/${editingEmployee}/`, formData);
                setSuccess("Empleado actualizado exitosamente.");
            } else {
                await api.post('/usuarios/', formData);
                setSuccess("Empleado creado exitosamente. Se han enviado las credenciales a su correo.");
            }
            handleCloseModal();
            fetchEmployees(currentPage, searchTerm);
        } catch (err) {
            console.error(err);
            let errorMsg = "Error al crear empleado. Verifique los datos.";
            if (err.response && err.response.data) {
                const data = err.response.data;
                if (data.error === 'gmail_not_linked') {
                    errorMsg = data.detail;
                } else if (data.email) errorMsg = `Error en Email: ${data.email[0]}`;
                else if (data.first_name) errorMsg = `Error en Nombre: ${data.first_name[0]}`;
                else if (data.detail) errorMsg = data.detail;
                else {
                    errorMsg = Object.values(data).flat()[0] || errorMsg;
                }
            }
            setError(errorMsg);
        } finally {
            setSubmissionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de desactivar este usuario?")) {
            try {
                await api.delete(`/usuarios/${id}/`);
                fetchEmployees(currentPage, searchTerm);
            } catch (err) {
                alert("Error al desactivar usuario.");
            }
        }
    };

    const handleResetPassword = async (id) => {
        if (window.confirm("¿Estás seguro de resetear la contraseña de este usuario? Se generará una nueva y se enviará por correo.")) {
            try {
                await api.post(`/usuarios/${id}/reset_password/`);
                setSuccess("Contraseña reseteada exitosamente. Correo enviado.");
                setTimeout(() => setSuccess(null), 3000);
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.detail || err.response?.data?.error || "Error al resetear contraseña.");
            }
        }
    };

    // Ya no necesitamos filtrado local
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const [tourSteps, setTourSteps] = useState([
        {
            element: '#tour-empleados-header',
            intro: 'Bajo esta pestaña, controlas a tus empleados. Puedes decidir quién es Administrador o quién es Vendedor.',
            position: 'bottom',
        }
    ]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'empleados') {
            // Unir la experiencia. Mostramos primero la tabla y luego señalamos el botón de Nuevo Empleado.
            setTourSteps([
                {
                    element: '#tour-empleados-header',
                    intro: '<strong>Tu Equipo de Trabajo</strong>Aquí puedes ver y organizar a todas las personas que usan el sistema. Podrás quitarles el acceso o cambiarles la contraseña cuando lo necesites.',
                    position: 'bottom',
                },
                {
                    element: '#tour-empleado-nuevo-btn',
                    intro: '<strong>Invitar Compañero</strong>Agrega a tus vendedores u otros administradores. El sistema les enviará un correo con su contraseña lista para usar.',
                    position: 'left',
                }
            ]);

            // Damos un milisegundo para que React renderice los botones antes de disparar AppTour
            setTimeout(() => {
                setRunTour(true);
            }, 500);
        }
    }, [location.search]);

    const handleTourComplete = () => {
        setShowModal(false);
        window.location.href = '/app/configuracion?tour=configuracion';
    };

    if (loading && employees.length === 0) return <div className="loading-overlay"><div className="spinner"></div></div>;

    return (
        <div className="container-custom fade-in-up" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', overflowX: 'hidden', overflowY: 'auto' }}>
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={tourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Configuraciones' }}
                    onTourComplete={handleTourComplete}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '0', borderBottom: 'none' }} id="tour-empleados-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Gestión de Empleados</h2>
                        <SectionHelpModal
                            title="¿Cómo funciona el Control de Personal?"
                            content={
                                <div>
                                    <p>Aquí administras las cuentas de las personas que trabajan en tu empresa. Es importante asignarles el nivel correcto:</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Perfil Administrador (ADMIN):</strong> Tiene el control total. Puede facturar, aprobar solicitudes, cambiar los ajustes del sistema y ver todas las cotizaciones del negocio.</li>
                                        <li><strong>Perfil Vendedor (ASESOR):</strong> Es un acceso limitado. Solo puede crear sus propias cotizaciones e interactuar con sus propios clientes. Si intenta hacer un descuento mayor al permitido, el sistema lo bloqueará y te enviará una solicitud de autorización.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Administra el acceso y roles de los miembros de tu equipo</p>
                </div>
                <button id="tour-empleado-nuevo-btn" className="btn-custom btn-primary d-none d-lg-flex" onClick={() => { 
                    setError(null);
                    setSuccess(null);
                    setEditingEmployee(null); 
                    setFormData({ first_name: '', last_name: '', email: '' }); 
                    setShowModal(true); 
                }}>
                    <FiPlus size={20} /> Nuevo Empleado
                </button>
            </div>


            <div className="card-custom">
                {/* Search Header */}
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                    <div className="header-search" style={{ flex: 1 }}>
                        <FiSearch size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o email..."
                            className="header-search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                {employees.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <FiUser size={48} color="var(--gray-400)" />
                        <p className="text-muted" style={{ marginTop: '10px' }}>No se encontraron empleados</p>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive d-none d-lg-block" style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="table-custom" style={{ minWidth: '950px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '25%' }}>Nombre</th>
                                        <th style={{ width: '25%' }}>Email</th>
                                        <th style={{ width: '10%' }}>Rol</th>
                                        <th style={{ width: '10%' }}>Estado</th>
                                        <th style={{ width: '15%' }}>Fecha Registro</th>
                                        <th style={{ width: '15%' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.map(emp => (
                                        <tr key={emp.id} onClick={() => handleEdit(emp)} style={{ cursor: 'pointer' }}>
                                            <td>
                                                <div style={{ fontWeight: '500' }}>{emp.first_name} {emp.last_name}</div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px' }}>
                                                    <FiMail size={12} color="var(--gray-600)" /> {emp.email}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg={emp.rol === 'ADMIN' ? 'info' : 'secondary'} style={{ fontWeight: 500 }}>
                                                    {emp.rol === 'ADMIN' && <FiShield className="me-1" />}
                                                    {emp.rol}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg={emp.is_active ? 'success' : 'danger'}>
                                                    {emp.is_active ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <FiCalendar size={12} color="var(--gray-400)" />
                                                    {emp.date_joined ? new Date(emp.date_joined).toLocaleDateString() : '-'}
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                                                    {emp.rol !== 'ADMIN' && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn-custom btn-sm"
                                                                onClick={() => handleResetPassword(emp.id)}
                                                                disabled={!emp.is_active}
                                                                title="Resetear Contraseña"
                                                                style={{ backgroundColor: '#fff', color: 'var(--warning-yellow)', border: '1px solid var(--warning-yellow)', padding: '4px 8px', fontSize: '13px' }}
                                                            >
                                                                <FiKey size={14} /> Resetear
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn-custom btn-sm"
                                                                onClick={() => handleDelete(emp.id)}
                                                                disabled={!emp.is_active}
                                                                title="Desactivar Usuario"
                                                                style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)', padding: '4px 8px', fontSize: '13px' }}
                                                            >
                                                                <FiUserX size={14} /> Desactivar
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-block d-lg-none mt-3">
                            {employees.map(emp => (
                                <div key={emp.id} className="mobile-card" onClick={() => handleEdit(emp)} style={{ cursor: 'pointer' }}>
                                    <div className="mobile-card-header">
                                        <span className="mobile-card-title" style={{ marginBottom: 0, fontSize: '15px' }}>{emp.first_name} {emp.last_name}</span>
                                        <Badge bg={emp.is_active ? 'success' : 'danger'}>
                                            {emp.is_active ? 'Activo' : 'Inactivo'}
                                        </Badge>
                                    </div>
                                    <div className="mobile-card-body">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
                                            <FiMail size={14} color="var(--gray-600)" /> {emp.email}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <Badge bg={emp.rol === 'ADMIN' ? 'info' : 'secondary'} style={{ fontWeight: 500 }}>
                                                {emp.rol === 'ADMIN' && <FiShield className="me-1" />}
                                                {emp.rol}
                                            </Badge>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--gray-500)' }}>
                                                <FiCalendar size={12} />
                                                {emp.date_joined ? new Date(emp.date_joined).toLocaleDateString() : '-'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mobile-card-footer" style={{ borderTop: 'none', paddingTop: 0, justifyContent: 'flex-end', minHeight: 'unseT', paddingBottom: '16px' }} onClick={(e) => e.stopPropagation()}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {emp.rol !== 'ADMIN' && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={() => handleResetPassword(emp.id)}
                                                        disabled={!emp.is_active}
                                                        title="Resetear Contraseña"
                                                        style={{ backgroundColor: '#fff', color: 'var(--warning-yellow)', border: '1px solid var(--warning-yellow)', padding: '4px 8px', fontSize: '13px' }}
                                                    >
                                                        <FiKey size={14} /> Resetear
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={() => handleDelete(emp.id)}
                                                        disabled={!emp.is_active}
                                                        title="Desactivar Usuario"
                                                        style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)', padding: '4px 8px', fontSize: '13px' }}
                                                    >
                                                        <FiUserX size={14} /> Desactivar
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                )}
            </div>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none', padding: '20px 24px pb-0' }}>
                    <Modal.Title style={{ fontWeight: 600 }}>{editingEmployee ? 'Editar Empleado' : 'Nuevo Empleado'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body id="tour-empleado-nuevo" style={{ padding: '0 24px 20px' }}>
                        {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-3">{success}</Alert>}
                        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">{error}</Alert>}

                        {!editingEmployee && !error && !success && (
                            <Alert variant="info" className="small d-flex align-items-center">
                                <FiAlertTriangle className="me-2" size={16} />
                                <div>
                                    El sistema generará una <strong>contraseña segura</strong> y la enviará al correo del empleado.
                                </div>
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Nombre</Form.Label>
                            <Form.Control
                                type="text"
                                name="first_name"
                                required
                                value={formData.first_name}
                                onChange={handleInputChange}
                                className="form-control-custom"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Apellido</Form.Label>
                            <Form.Control
                                type="text"
                                name="last_name"
                                required
                                value={formData.last_name}
                                onChange={handleInputChange}
                                className="form-control-custom"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Correo Electrónico</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleInputChange}
                                className="form-control-custom"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none', padding: '0 24px 24px' }}>
                        <button
                            type="button"
                            className="btn-custom"
                            style={{ background: '#f8f9fa', color: '#333', border: '1px solid #ddd' }}
                            onClick={handleCloseModal}
                        >
                            Cancelar
                        </button>
                        <button type="submit" className="btn-custom btn-primary" disabled={submissionLoading}>
                            {submissionLoading ? <Spinner size="sm" animation="border" /> : (editingEmployee ? 'Guardar Cambios' : 'Crear Empleado')}
                        </button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Botón Flotante para Móviles */}
            <button
                className="fab-custom d-lg-none"
                onClick={() => { setEditingEmployee(null); setFormData({ first_name: '', last_name: '', email: '' }); setShowModal(true); }}
                title="Nuevo Empleado"
            >
                <FiPlus size={28} />
            </button>
        </div>
    );
};

export default Employees;
